import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Home,
  Hammer,
  Gift,
  PenLine,
  CircleHelp,
  Send,
  ArrowDown,
  ArrowUpRight,
  BellRing,
  Handshake,
  MessageSquareText,
  CalendarClock,
  BadgeCheck,
  ShieldHalf,
  Building2,
  ShieldCheck,
  MailCheck,
  LoaderCircle,
} from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { PageHero } from "@/components/blocks/PageHero";
import { HeroCard } from "@/components/blocks/HeroCard";
import { SectionHeading } from "@/components/blocks/SectionHeading";
import { StatusPhases } from "@/components/blocks/StatusPhases";
import { FaqList } from "@/components/blocks/FaqList";
import { CTABand } from "@/components/blocks/CTABand";
import {
  FIELD,
  LABEL,
  ChipGroup,
  Checkbox,
  Honeypot,
  FormError,
} from "@/components/blocks/FormParts";
import {
  API_WAITLIST,
  postJson,
  useSpamGuard,
  formErrorMessage,
  revealElement,
} from "@/lib/forms";
import { Reveal } from "@/components/Reveal";
import {
  CONTACT_EMAIL,
  ROUTE_CONTACT,
  ROUTE_DEMO,
  ROUTE_FAQ,
  ROUTE_WAITLIST,
} from "@/lib/brand";
import { FOUNDER_MONTHS, FOUNDER_UNTIL } from "@/lib/pricing";
import { FAQ_BY_ID } from "@/lib/faq";
import { STATUS_LAUNCH, STATUS_UPDATED } from "@/lib/status";

const SIDEBAR_ITEMS = [
  { id: "top", label: "Start", icon: Home },
  { id: "stand", label: "Stand", icon: Hammer },
  { id: "vorteile", label: "Was ihr bekommt", icon: Gift },
  { id: "formular", label: "Platz vormerken", icon: PenLine },
  { id: "fragen", label: "Fragen", icon: CircleHelp },
  { id: "cta", label: "Demo", icon: Send },
];

const AT_A_GLANCE = [
  {
    icon: CalendarClock,
    title: `Start ${STATUS_LAUNCH.in}`,
    text: "Dann öffnen Registrierung und Login für Vereine und Unternehmen.",
  },
  {
    icon: Handshake,
    title: "Persönlich begleitet",
    text: "Den Einstieg richten wir mit den ersten Vereinen und Unternehmen gemeinsam ein.",
  },
  {
    icon: BadgeCheck,
    title: "Kostenlos und unverbindlich",
    text: "Kein Konto, kein Abo, keine Zahlungsdaten.",
  },
  {
    icon: MessageSquareText,
    title: "Mitreden",
    text: "Wer möchte, testet vor dem Start und sagt uns, was fehlt.",
  },
];

// Der erste Punkt hängt am Preismodell: bei Änderungen in pricing.js prüfen.
const BENEFITS = [
  {
    icon: BellRing,
    title: "Als Erste Bescheid",
    text: `Ihr erfahrt vor allen anderen, wann es losgeht, und könnt den Gründerpreis vom ersten Tag an nutzen: ${FOUNDER_MONTHS} Monate günstiger für alle Abschlüsse bis zum ${FOUNDER_UNTIL}.`,
    link: { label: "Zur Preisstruktur", to: "/preisstruktur" },
  },
  {
    icon: Handshake,
    title: "Gemeinsam einrichten",
    text: "Die ersten Vereine und Unternehmen begleiten wir persönlich: Profil anlegen, erstes Sponsoring einrichten, bestehende Partner mitnehmen. Am Telefon oder am Bildschirm, Schritt für Schritt.",
  },
  {
    icon: MessageSquareText,
    title: "Mitreden",
    text: "Wer möchte, testet MatchGate vor dem Start und sagt uns, was fehlt oder stört. Ein Häkchen im Formular reicht, wir melden uns dann bei euch.",
  },
];

const ROLES = [
  { value: "verein", label: "Verein", icon: ShieldHalf, org: "TSV Musterstadt" },
  { value: "unternehmen", label: "Unternehmen", icon: Building2, org: "Muster GmbH" },
];

// Gleichlautend mit CONSENT_TEXT in public/api/bestaetigen.php
const CONSENT_TEXT =
  "Ich bin einverstanden, dass MatchGate mich per E-Mail über den Start der Plattform benachrichtigt. Die Einwilligung kann ich jederzeit widerrufen.";
const PILOT_TEXT =
  "Ich würde MatchGate gern vor dem Start testen und Rückmeldung geben.";

// Die Frage nach dem Start verweist sonst auf diese Seite selbst.
const FAQ_ITEMS = FAQ_BY_ID.start.items.map(({ link, ...item }) =>
  link && link.to !== ROUTE_WAITLIST ? { ...item, link } : item
);

const BTN_PRIMARY =
  "group inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-white bg-gradient-to-r from-brand-orange to-brand-blue shadow-[0_0_35px_-6px_rgba(255,107,0,0.7)] hover:shadow-[0_0_45px_-4px_rgba(0,122,255,0.8)] hover:scale-[1.03] transition-all duration-300";

const linkClass =
  "text-white/75 underline decoration-white/25 underline-offset-4 hover:text-white hover:decoration-brand-orange transition-colors duration-300";

// Beschriftungen für Fehlermeldungen des Skripts (public/api/warteliste.php)
const FIELD_LABELS = {
  role: "Vormerken als",
  name: "Name",
  organisation: "Verein oder Unternehmen",
  place: "PLZ oder Ort",
  email: "E-Mail",
  consent: "Einwilligung",
};

const FORM_CARD =
  "relative overflow-hidden rounded-[2rem] border border-white/10 backdrop-blur-xl p-7 md:p-10";

const WaitlistForm = ({ initialRole }) => {
  const [form, setForm] = useState({
    role: initialRole,
    name: "",
    organisation: "",
    place: "",
    email: "",
    pilot: false,
    consent: false,
  });
  // idle | sending | sent
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const guard = useSpamGuard();
  const sentRef = useRef(null);

  useEffect(() => {
    if (status === "sent") revealElement(sentRef.current);
  }, [status]);

  const update = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  const role = ROLES.find((r) => r.value === form.role) ?? ROLES[0];

  // Schritt 1 des Double-Opt-In: Das Skript schickt eine Mail mit
  // Bestätigungslink; erst die Seite /warteliste/bestaetigen trägt ein.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    setError("");
    const result = await postJson(API_WAITLIST, { ...form, ...guard.fields() });
    if (result.ok) {
      setStatus("sent");
    } else {
      setError(formErrorMessage(result, FIELD_LABELS));
      setStatus("idle");
    }
  };

  const startOver = () => {
    setForm((prev) => ({ ...prev, email: "" }));
    guard.reset();
    setStatus("idle");
  };

  if (status === "sent") {
    return (
      <div
        ref={sentRef}
        id="warteliste-gesendet"
        data-testid="waitlist-sent"
        className={`${FORM_CARD} scroll-mt-header`}
      >
        <div className="pointer-events-none absolute -top-24 -right-20 w-72 h-72 rounded-full bg-brand-orange/15 blur-[110px]" />
        <div className="relative">
          <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/[0.06] border border-white/10 mb-6">
            <MailCheck className="w-5 h-5 text-brand-orange" />
          </span>
          <h3 className="font-display font-bold text-2xl md:text-3xl text-white">
            Fast geschafft: Bitte bestätigt euren Eintrag.
          </h3>
          <p className="mt-4 max-w-lg text-base leading-relaxed text-white/60">
            Wir haben euch eine E-Mail an{" "}
            <span className="text-white/85">{form.email}</span> geschickt. Erst
            mit dem Klick auf den Link darin steht ihr auf der Warteliste.
          </p>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-white/45">
            Nichts angekommen? Schaut im Spam-Ordner nach oder schreibt uns an{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className={linkClass}>
              {CONTACT_EMAIL}
            </a>
            .
          </p>
          <button
            type="button"
            onClick={startOver}
            className="mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-white/80 border border-white/15 bg-white/[0.02] hover:bg-white/[0.06] hover:text-white transition-all duration-300"
          >
            Andere Adresse eintragen
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="waitlist-form" className={FORM_CARD}>
      <div className="pointer-events-none absolute -top-24 -right-20 w-72 h-72 rounded-full bg-brand-orange/15 blur-[110px]" />
      <Honeypot id="waitlist-website" {...guard.honeypot} />

      <div className="relative space-y-6">
        <ChipGroup
          label="Vormerken als"
          options={ROLES}
          value={form.role}
          onChange={set("role")}
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label className={LABEL} htmlFor="waitlist-name">
              Name
            </label>
            <input
              id="waitlist-name"
              className={FIELD}
              type="text"
              required
              maxLength={200}
              autoComplete="name"
              placeholder="Vor- und Nachname"
              value={form.name}
              onChange={update("name")}
            />
          </div>
          <div>
            <label className={LABEL} htmlFor="waitlist-org">
              {role.label}
            </label>
            <input
              id="waitlist-org"
              className={FIELD}
              type="text"
              required
              maxLength={200}
              autoComplete="organization"
              placeholder={role.org}
              value={form.organisation}
              onChange={update("organisation")}
            />
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label className={LABEL} htmlFor="waitlist-place">
              PLZ oder Ort
            </label>
            <input
              id="waitlist-place"
              className={FIELD}
              type="text"
              required
              maxLength={200}
              autoComplete="postal-code"
              placeholder="12345 Musterstadt"
              value={form.place}
              onChange={update("place")}
            />
          </div>
          <div>
            <label className={LABEL} htmlFor="waitlist-email">
              E-Mail
            </label>
            <input
              id="waitlist-email"
              className={FIELD}
              type="email"
              required
              maxLength={254}
              autoComplete="email"
              placeholder="name@beispiel.de"
              value={form.email}
              onChange={update("email")}
            />
          </div>
        </div>

        <div className="space-y-4 pt-2">
          <Checkbox id="waitlist-pilot" checked={form.pilot} onChange={set("pilot")}>
            {PILOT_TEXT} <span className="text-white/35">(freiwillig)</span>
          </Checkbox>
          <Checkbox
            id="waitlist-consent"
            checked={form.consent}
            onChange={set("consent")}
            required
          >
            {CONSENT_TEXT} Mehr dazu in der{" "}
            <Link to="/datenschutz" className={linkClass}>
              Datenschutzerklärung
            </Link>
            .
          </Checkbox>
        </div>

        <FormError>{error}</FormError>

        <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-1">
          <button
            type="submit"
            data-testid="waitlist-submit"
            disabled={status === "sending"}
            className={`${BTN_PRIMARY} shrink-0 disabled:opacity-70 disabled:pointer-events-none`}
          >
            {status === "sending" ? "Wird gesendet …" : "Platz vormerken"}
            {status === "sending" ? (
              <LoaderCircle className="w-4 h-4 animate-spin" />
            ) : (
              <BellRing className="w-4 h-4 group-hover:rotate-12 transition-transform duration-300" />
            )}
          </button>
          <p className="text-xs leading-relaxed text-white/40">
            Ihr bekommt gleich eine E-Mail mit einem Bestätigungslink. Erst damit
            steht ihr auf der Liste.
          </p>
        </div>
      </div>
    </form>
  );
};

export default function Waitlist() {
  const [params] = useSearchParams();
  const initialRole = params.get("rolle") === "unternehmen" ? "unternehmen" : "verein";

  return (
    <PageLayout sidebarItems={SIDEBAR_ITEMS} title="Warteliste" testId="waitlist-main">
      <PageHero
        eyebrow="Warteliste"
        titleLines={["Seid dabei,", "wenn MatchGate"]}
        titleAccent="an den Start geht."
        lead={`MatchGate startet ${STATUS_LAUNCH.in}. Merkt euch jetzt einen Platz vor: Ihr erfahrt als Erste, wann es losgeht, und wir richten den Einstieg gemeinsam mit euch ein.`}
        primary={{ label: "Platz vormerken", href: "#formular", icon: ArrowDown }}
        secondary={{ label: "Demo ansehen", to: ROUTE_DEMO }}
      >
        <HeroCard kicker="Die Warteliste auf einen Blick" items={AT_A_GLANCE} />
      </PageHero>

      {/* Stand */}
      <section id="stand" data-testid="waitlist-status-section" className="relative py-24 lg:py-32">
        <div className="px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-sticky">
              <SectionHeading
                eyebrow="Stand"
                title="Wo MatchGate gerade "
                accent="steht"
                tail="."
                lead={`Konzept, Messlogik und Demo sind fertig, die Plattform selbst bauen wir gerade. Registrieren könnt ihr euch deshalb noch nicht, einen Platz vormerken schon. Stand: ${STATUS_UPDATED}.`}
                className="max-w-md mb-0"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.05}>
              <div className="gradient-border rounded-[2rem] backdrop-blur-xl p-7 md:p-10">
                <span className="block text-[11px] uppercase tracking-[0.2em] font-bold text-white/40 mb-7">
                  Der Weg zum Start
                </span>
                <StatusPhases />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Was ihr bekommt */}
      <section
        id="vorteile"
        data-testid="waitlist-benefits-section"
        className="relative py-24 lg:py-32 border-t border-white/[0.06]"
      >
        <div className="px-6 md:px-10 lg:px-16">
          <SectionHeading
            eyebrow="Was ihr bekommt"
            title="Was euch ein Platz auf der Liste "
            accent="bringt"
            tail="."
            lead="Mehr als eine Benachrichtigung: Wer früh dabei ist, gestaltet den Start mit."
          />

          <div className="grid gap-5 md:gap-6 lg:grid-cols-3">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.title} delay={i * 0.07}>
                  <div className="group relative h-full flex flex-col overflow-hidden rounded-3xl border border-white/10 backdrop-blur-xl p-8 hover:border-white/20 transition-colors duration-500">
                    <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/[0.06] border border-white/10 mb-6">
                      <Icon className="w-5 h-5 text-brand-orange" />
                    </span>
                    <h3 className="font-display font-bold text-xl text-white mb-3">
                      {b.title}
                    </h3>
                    <p className="text-base leading-relaxed text-white/55">{b.text}</p>
                    {b.link && (
                      <div className="mt-auto pt-7">
                        <Link
                          to={b.link.to}
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/50 hover:text-white transition-colors duration-300"
                        >
                          {b.link.label}
                          <ArrowUpRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-brand-orange to-brand-blue group-hover:w-full transition-all duration-700" />
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Formular */}
      <section
        id="formular"
        data-testid="waitlist-form-section"
        className="relative py-24 lg:py-32 border-t border-white/[0.06]"
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
          <div className="absolute top-0 left-1/4 w-[560px] h-[420px] rounded-full bg-brand-blue/10 blur-[170px]" />
        </div>

        <div className="px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-sticky">
              <SectionHeading
                eyebrow="Platz vormerken"
                title="Ein paar Angaben, "
                accent="fertig"
                tail="."
                lead="Mehr brauchen wir nicht, um euch zum Start Bescheid zu geben und den Einstieg vorzubereiten."
                className="max-w-md mb-8"
              />

              <Reveal delay={0.1}>
                <div className="space-y-4">
                  <div className="flex items-start gap-4 rounded-2xl border border-white/10 backdrop-blur-xl px-5 py-4">
                    <span className="flex items-center justify-center w-10 h-10 shrink-0 rounded-xl bg-white/[0.06] border border-white/10">
                      <BadgeCheck className="w-[18px] h-[18px] text-brand-orange" />
                    </span>
                    <div>
                      <div className="text-sm font-semibold text-white">
                        Kostenlos und unverbindlich
                      </div>
                      <p className="mt-0.5 text-sm leading-relaxed text-white/50">
                        Ihr schließt nichts ab und entscheidet zum Start in
                        Ruhe, ob ihr dabei seid.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 rounded-2xl border border-white/10 backdrop-blur-xl px-5 py-4">
                    <span className="flex items-center justify-center w-10 h-10 shrink-0 rounded-xl bg-white/[0.06] border border-white/10">
                      <ShieldCheck className="w-[18px] h-[18px] text-brand-blue" />
                    </span>
                    <div>
                      <div className="text-sm font-semibold text-white">
                        Nur für den Start
                      </div>
                      <p className="mt-0.5 text-sm leading-relaxed text-white/50">
                        Eure Angaben nutzen wir nur dafür. Ihr bestätigt den
                        Eintrag per Mail, abmelden geht jederzeit.
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.05}>
              <WaitlistForm key={initialRole} initialRole={initialRole} />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Fragen */}
      <section
        id="fragen"
        data-testid="waitlist-faq-section"
        className="relative py-24 lg:py-32 border-t border-white/[0.06]"
      >
        <div className="px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5 xl:col-span-4">
            <div className="lg:sticky lg:top-sticky">
              <SectionHeading
                eyebrow="Fragen"
                title="Gut zu "
                accent="wissen"
                tail="."
                lead="Was vor dem Eintragen oft gefragt wird. Alles Weitere steht bei den häufigen Fragen."
                className="max-w-sm mb-6"
              />
              <Link
                to={ROUTE_FAQ}
                className="group inline-flex items-center gap-2 text-sm font-bold text-white"
              >
                Alle häufigen Fragen
                <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-7 xl:col-span-8">
            <FaqList items={FAQ_ITEMS} />
          </div>
        </div>
      </section>

      <CTABand
        title="Lieber erst "
        accent="anschauen"
        tail="?"
        lead="In der Demo stellt ihr ein Beispiel-Sponsoring zusammen und seht, was daraus entsteht. Fragen beantworten wir gern persönlich."
        primary={{ label: "Demo ansehen", to: ROUTE_DEMO }}
        secondary={{ label: "Kontakt aufnehmen", to: ROUTE_CONTACT }}
      />
    </PageLayout>
  );
}
